import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Button, Table, Modal, Badge } from 'react-bootstrap';
import { BsPlusCircleFill, BsPencilSquare, BsTrash } from 'react-icons/bs';
import DepartmentSidebar from './components/DepertmentSidebar';
import DepartmentTopbar from './components/DepartmentTopbar';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function CashDailyChalan() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [funds, setFunds] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedFund, setSelectedFund] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentType, setPaymentType] = useState('');
  const [notes, setNotes] = useState('');

  const [chalans, setChalans] = useState([]);
  const [editingChalanId, setEditingChalanId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [chalanNo, setChalanNo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [noDataMessage, setNoDataMessage] = useState('');

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_catagory_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const formattedCategories = response.data.data.map((cat) => ({
          id: cat.id,
          catagory_name: cat.catagory_name,
        }));
        setCategories(formattedCategories);
      } else {
        toast.error(`Error fetching categories: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_subcatagory_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSubCategories(response.data.data);
      } else {
        toast.error(`Error fetching subcategories: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to fetch subcategories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFunds = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}fundlist.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const formattedFunds = response.data.funds.map((fund) => ({
          id: parseInt(fund.id, 10),
          name: fund.fund_name,
          total: parseFloat(fund.amount) || 0,
        }));
        setFunds(formattedFunds);
      } else {
        toast.error(`Error fetching funds: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching funds:', error);
      toast.error('Failed to fetch funds. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchChalans();
    fetchFunds();
  }, []);

  const filteredSubCategories = subCategories.filter(sub => sub.catagory_id === selectedCategory);

  const filteredChalans = chalans.filter(chalan => {
    const matchesSearch = chalan.chalanNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chalan.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chalan.subCategoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chalan.fundName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chalan.amount.toString().includes(searchTerm) ||
      chalan.date?.includes(searchTerm) ||
      chalan.paymentType?.toLowerCase().includes(searchTerm);

    const matchesDateRange = (!startDate || chalan.date >= startDate) &&
      (!endDate || chalan.date <= endDate);

    return matchesSearch && matchesDateRange;
  });

  const fetchChalans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_chalan_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        const formattedChalans = response.data.data.map((item) => ({
          id: item.id,
          chalanNo: item.chalan_no,
          categoryId: item.catagory_id,
          subCategoryId: item.subcatagory_id,
          fundId: item.fund_id,
          amount: parseFloat(item.amount),
          date: item.date,
          paymentType: item.payment_type,
          notes: item.note,
          categoryName: item.category_name,
          subCategoryName: item.subcategory_name,
          fundName: item.fund_name,
          status: item.status
        }));
        formattedChalans.sort((a, b) => new Date(b.date) - new Date(a.date));
        setChalans(formattedChalans);
        setNoDataMessage('');
      } else {
        if (response.data.message === 'No cash chalans found.') {
          setChalans([]);
          setNoDataMessage('No cash chalans found.');
        } else {
          console.error(`Error fetching chalans: ${response.data.message}`);
        }
      }
    } catch (error) {
      console.error('Error fetching chalans:', error);
      // Removed toast error as per user request
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCategory || !selectedFund || !amount || !paymentType) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingChalanId) {
        // Update existing chalan
        const updatePayload = {
          id: editingChalanId,
          chalan_no: chalanNo,
          catagory_id: selectedCategory,
          subcatagory_id: selectedSubCategory,
          fund_id: selectedFund,
          amount: amount,
          date: date,
          payment_type: paymentType,
          note: notes || '',
        };

        const updateResponse = await axios.post(
          `${API_URL}update_cash_chalan.php`,
          updatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (updateResponse.data.status === 'success') {
          fetchChalans();
          setChalanNo('');
          setSelectedCategory('');
          setSelectedSubCategory('');
          setSelectedFund('');
          setAmount('');
          setDate(new Date().toISOString().slice(0, 10));
          setPaymentType('');
          setNotes('');
          setEditingChalanId(null);
          setShowModal(false);

          toast.success('Chalan updated successfully!');
        } else {
          throw new Error(updateResponse.data.message || 'Failed to update chalan');
        }
      } else {
        const payload = {
          chalan_no: chalanNo,
          catagory_id: selectedCategory,
          subcatagory_id: selectedSubCategory,
          fund_id: selectedFund,
          amount: amount,
          date: date,
          payment_type: paymentType,
          note: notes || ''
        };

        const response = await axios.post(
          `${API_URL}add_cash_chalan.php`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data.status === 'success') {
          // Refetch the chalans list
          fetchChalans();

          // Reset form
          setChalanNo('');
          setSelectedCategory('');
          setSelectedSubCategory('');
          setSelectedFund('');
          setAmount('');
          setDate(new Date().toISOString().slice(0, 10));
          setPaymentType('');
          setNotes('');
          setShowModal(false);

          toast.success('Chalan created successfully!');
          navigate('/departmentDashboard/cash-daily-chalan');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          throw new Error(response.data.message || 'Failed to create chalan');
        }
      }
    } catch (error) {
      console.error('Error creating chalan:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (chalan) => {
    setChalanNo(chalan.chalanNo || '');
    setSelectedCategory(chalan.categoryId || '');
    setSelectedSubCategory(chalan.subCategoryId || '');
    setSelectedFund(chalan.fundId || '');
    setAmount(chalan.amount.toString());
    setDate(chalan.date || new Date().toISOString().slice(0, 10));
    setPaymentType(chalan.paymentType || '');
    setNotes(chalan.notes || '');
    setEditingChalanId(chalan.id);
    setShowModal(true);
  };

  // Calculate totals
  const pendingTotal = filteredChalans
    .filter(chalan => chalan.status === "0")
    .reduce((sum, chalan) => sum + chalan.amount, 0);

  const approvedTotal = filteredChalans
    .filter(chalan => chalan.status === "1")
    .reduce((sum, chalan) => sum + chalan.amount, 0);

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3 d-flex align-items-center gap-2">
              Cash Daily Chalan
            </h1>
          </header>

          <Row className="g-4 mb-4">
            <Col lg={12}>
              <Card className="shadow-sm">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    backgroundColor: "#e3f2fd",
                    borderBottom: "2px solid #1976d2",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <BsPlusCircleFill style={{ color: "#1976d2" }} />{" "}
                    <span style={{ color: "#1976d2" }}>
                      Cash Chalan List
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Search chalans..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: '200px' }}
                    />
                    <Form.Control
                      type="date"
                      placeholder="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ width: '150px' }}
                    />
                    <Form.Control
                      type="date"
                      placeholder="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{ width: '150px' }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => {
                        // Reset all form fields for new chalan
                        setChalanNo('');
                        setSelectedCategory('');
                        setSelectedSubCategory('');
                        setSelectedFund('');
                        setAmount('');
                        setDate(new Date().toISOString().slice(0, 10));
                        setPaymentType('');
                        setNotes('');
                        setEditingChalanId(null);
                        setShowModal(true);
                      }}
                    >
                      <BsPlusCircleFill />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover responsive="sm">
                      <thead className="table-dark" style={{ backgroundColor: "#0d6efd" }}>
                        <tr>
                          <th>Chalan No</th>
                          <th>Category</th>
                          <th>Sub Category</th>
                          <th>Fund</th>
                          <th>Amount</th>
                          <th>Date</th>
                          <th>Payment Type</th>
                          <th>Note</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredChalans.map((chalan) => (
                          <tr key={chalan.id}>
                            <td>{chalan.chalanNo}</td>
                            <td>{chalan.categoryName}</td>
                            <td>{chalan.subCategoryName}</td>
                            <td>{chalan.fundName}</td>
                            <td>₹{chalan.amount.toLocaleString()}</td>
                            <td>{chalan.date}</td>
                            <td>{chalan.paymentType}</td>
                            <td>{chalan.notes}</td>
                            <td><Badge bg={chalan.status === "1" ? "success" : "secondary"}>{chalan.status === "1" ? "Approved" : "Pending"}</Badge></td>
                            <td className="d-flex gap-2">
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleEdit(chalan)}
                                disabled={chalan.status === "1"}
                              >
                                <BsPencilSquare />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {filteredChalans.length === 0 && noDataMessage && (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              {noDataMessage}
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                          <td colSpan="4" style={{ textAlign: 'right' }}>Total Pending:</td>
                          <td>₹{pendingTotal.toLocaleString()}</td>
                          <td colSpan="5"></td>
                        </tr>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                          <td colSpan="4" style={{ textAlign: 'right' }}>Total Approved:</td>
                          <td>₹{approvedTotal.toLocaleString()}</td>
                          <td colSpan="5"></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingChalanId ? 'Edit' : 'Add'} Cash Chalan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Chalan No</Form.Label>
                  <Form.Control
                    type="text"
                    value={chalanNo}
                    onChange={(e) => setChalanNo(e.target.value)}
                    placeholder="Enter Chalan No"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Category</Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedSubCategory('');
                    }}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat?.catagory_name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Sub Category</Form.Label>
                  <Form.Select
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                    disabled={!selectedCategory}
                  >
                    <option value="">Select Sub Category</option>
                    {filteredSubCategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.subcatagory_name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Fund</Form.Label>
                  <Form.Select
                    value={selectedFund}
                    onChange={(e) => setSelectedFund(e.target.value)}
                    required
                  >
                    <option value="">Select Fund</option>
                    {funds.map(fund => (
                      <option key={fund.id} value={fund.id}>{fund.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAmount(value);
                      }
                    }}
                    placeholder="Enter amount (e.g. 5555 or 55.79)"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Type</Form.Label>
                  <Form.Select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    required
                  >
                    <option value="">Select Payment Type</option>
                    <option value="Online">Online</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter notes"
              />
            </Form.Group>
            <div className="d-grid">
              <Button variant="success" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : (editingChalanId ? 'Update' : 'Submit')} Chalan
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CashDailyChalan;
